import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import {
  adminPromoCampaignSchema,
  adminPromoRedemptionSchema,
  type AdminPromoCampaignView,
  type AdminPromoCreateInput,
  type AdminPromoRedemptionView,
  type AdminPromoUpdateInput,
} from '@steado/admin-contract/promotions';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

import type { PromotionDetailSearch } from './promotions-search';

export const REDEMPTIONS_PAGE_SIZE = 20;

/** Every key below this one is a promotions read; mutations invalidate it whole. */
export const PROMOTIONS_KEY = ['admin', 'promotions'] as const;

/** Not paginated: a handful of campaigns at a time is the whole point of them. */
export function promotionsQueryOptions(api: ApiClient, includeArchived: boolean) {
  return queryOptions({
    queryKey: [...PROMOTIONS_KEY, 'list', { includeArchived }],
    queryFn: (): Promise<AdminPromoCampaignView[]> =>
      api.request({
        path: '/admin/promotions',
        query: { includeArchived: String(includeArchived) },
        schema: z.array(adminPromoCampaignSchema),
      }),
    placeholderData: (previous) => previous,
  });
}

export function promotionQueryOptions(api: ApiClient, id: string) {
  return queryOptions({
    queryKey: [...PROMOTIONS_KEY, 'detail', id],
    queryFn: (): Promise<AdminPromoCampaignView> =>
      api.request({ path: `/admin/promotions/${id}`, schema: adminPromoCampaignSchema }),
  });
}

export interface RedemptionsPage {
  readonly items: AdminPromoRedemptionView[];
  readonly pagination: PaginationMeta;
}

const redemptionsPageSchema = z.object({
  items: z.array(adminPromoRedemptionSchema),
  pagination: paginationMetaSchema,
});

export function redemptionsQueryOptions(api: ApiClient, id: string, filters: PromotionDetailSearch) {
  const query: Record<string, QueryValue> = {
    status: filters.status,
    cursor: filters.cursor,
    limit: REDEMPTIONS_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: [...PROMOTIONS_KEY, 'redemptions', id, filters],
    queryFn: async (): Promise<RedemptionsPage> => {
      const response = await api.requestWithMeta({
        path: `/admin/promotions/${id}/redemptions`,
        query,
        schema: z.array(adminPromoRedemptionSchema),
      });

      return redemptionsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}

export function createPromotion(
  api: ApiClient,
  body: AdminPromoCreateInput,
): Promise<AdminPromoCampaignView> {
  return api.request({ path: '/admin/promotions', method: 'POST', body, schema: adminPromoCampaignSchema });
}

export function updatePromotion(
  api: ApiClient,
  id: string,
  body: AdminPromoUpdateInput,
): Promise<AdminPromoCampaignView> {
  return api.request({
    path: `/admin/promotions/${id}`,
    method: 'PATCH',
    body,
    schema: adminPromoCampaignSchema,
  });
}

/** Idempotent on the API: archiving an archived campaign returns it unchanged. */
export function archivePromotion(api: ApiClient, id: string): Promise<AdminPromoCampaignView> {
  return api.request({
    path: `/admin/promotions/${id}/archive`,
    method: 'POST',
    schema: adminPromoCampaignSchema,
  });
}
